// StartConversationModal.js
import React from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Autocomplete,
    Grid,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import { PapiroConsole } from "src/utils/papiroConsole";

const styles = (theme) => ({
    dialogTitle: {
        backgroundColor: "#3f51b4",
        color: "#ffffff",
        textAlign: "center",
        fontWeight: "bold",
        padding: "16px",
    },
    dialogContent: {
        backgroundColor: "#ffffff",
        padding: "24px",
        marginTop: "16px"
    },
    dialogActions: {
        backgroundColor: "#f5f5f5",
        padding: "16px",
    },
    section: {
        marginBottom: "16px",
    },
    sectionTitle: {
        fontSize: "1rem",
        fontWeight: "bold",
        color: "#3f51b4",
        marginTop: "16px",
        marginBottom: "8px",
    },
    sectionText: {
        backgroundColor: "#f3f3f3",
        padding: "12px",
        borderRadius: "4px",
        marginBottom: "8px",
        lineHeight: "1.6",
        wordBreak: "break-word",
    },
    gridContainer: {
        maxHeight: "400px",
        overflowY: "auto",
        padding: theme.spacing(2),
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#3F51B4',
            borderRadius: '8px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '8px',
        },
    },
    activeChatSection: {
        marginTop: "24px",
        borderTop: "1px solid #e0e0e0",
        paddingTop: "16px",
    },
    activeChatTitle: {
        fontSize: "1rem",
        fontWeight: "bold",
        color: "#3f51b4",
        marginBottom: "12px",
    },
    activeChatCard: {
        display: "flex",
        alignItems: "center",
        padding: "12px",
        marginBottom: "8px",
        backgroundColor: "#f3f3f3",
        borderRadius: "8px",
        cursor: "pointer",
        border: "1px solid transparent",
        transition: "all 0.2s ease",
        '&:hover': {
            backgroundColor: "#e8eaf6",
            borderColor: "#3f51b4",
        },
    },
    activeChatPhone: {
        fontWeight: "bold",
        color: "#3f51b4",
        marginRight: "8px",
    },
    activeChatLabel: {
        color: "#666",
        fontSize: "0.875rem",
    },
});

class StartConversationModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            // Seleções
            selectedRegistrationNumber: null,
            selectedMessageProvider: null,
            selectedTemplate: null,
            
            // Listas
            registrationNumberList: [],
            messageProviderList: [],
            templateList: [],
            
            // Variáveis do template
            bodyVariableValues: {},
            headerVariableValues: {},
            buttonVariables: {},
            
            // Preview
            previewTextBody: "",
            previewTextHeader: "",
            
            // Estados de loading
            loadingTemplates: false,
            loadingSubmit: false,
            
            // Controle
            canSubmit: false,
        };
    }

    componentDidUpdate(prevProps) {
        // Quando o modal abrir e tivermos dados de inicialização
        if (!prevProps.open && this.props.open && this.props.initializationData) {
            this.initializeModal();
        }
    }

    initializeModal = () => {
        const { initializationData } = this.props;
        
        if (!initializationData) return;

        // Propriedades em minúsculo conforme retorno da API
        const { registrationnumberrequestlist, messageproviderlist } = initializationData;

        // Pré-seleção do número registrado (se houver apenas 1)
        const selectedNumber = registrationnumberrequestlist?.length === 1 
            ? registrationnumberrequestlist[0] 
            : null;

        // Pré-seleção do provedor (se houver apenas 1)
        const selectedProvider = messageproviderlist?.length === 1 
            ? messageproviderlist[0] 
            : null;

        this.setState({
            registrationNumberList: registrationnumberrequestlist || [],
            messageProviderList: messageproviderlist || [],
            selectedRegistrationNumber: selectedNumber,
            selectedMessageProvider: selectedProvider,
            selectedTemplate: null,
            templateList: [],
            bodyVariableValues: {},
            headerVariableValues: {},
            buttonVariables: {},
            previewTextBody: "",
            previewTextHeader: "",
            canSubmit: false,
        }, () => {
            // Se já temos um número pré-selecionado, buscar templates
            if (selectedNumber) {
                this.loadTemplates(selectedNumber.id);
            }
        });
    };

    loadTemplates = async (registrationNumberRequestId) => {
        const { phoneNumber, userId, onError, validatePhoneApi, intl} = this.props;

        if (!registrationNumberRequestId || !phoneNumber) return;

        this.setState({ loadingTemplates: true, templateList: [], selectedTemplate: null });

        try {
            const data = {
                phone: phoneNumber,
                registrationnumberrequestid: registrationNumberRequestId,
                userid: userId || null
            };

            const result = await validatePhoneApi(data);

            if (result && result.success) {
                this.setState({ 
                    templateList: result.data || [],
                    canSubmit: true 
                });
            } else {
                PapiroConsole.log("error loadTemplates else ")
                PapiroConsole.log(result)
                const errorMessage = result && result.data && result.data.errors ? result.data.errors[0] : intl.formatMessage({ id: "process.error" })
                onError && onError(errorMessage);
                this.setState({ canSubmit: false });
            }
        } catch (error) {
          PapiroConsole.log("error loadTemplates");
          PapiroConsole.log(error);

          const errorMsg =
            error?.response?.data?.errors?.[0]
            ?? intl.formatMessage({ id: "process.error" });

          onError?.(errorMsg);
          this.setState({ canSubmit: false });
        }finally {
            this.setState({ loadingTemplates: false });
        }
    };

    handleRegistrationNumberChange = (event, value) => {
        this.setState({
            selectedRegistrationNumber: value,
            selectedTemplate: null,
            templateList: [],
            bodyVariableValues: {},
            headerVariableValues: {},
            buttonVariables: {},
            previewTextBody: "",
            previewTextHeader: "",
            canSubmit: false,
        }, () => {
            if (value?.id) {
                this.loadTemplates(value.id);
            }
        });
    };

    handleMessageProviderChange = (event, value) => {
        this.setState({ selectedMessageProvider: value });
    };

    handleTemplateChange = (event, value) => {
        if (!value) {
            this.setState({
                selectedTemplate: null,
                bodyVariableValues: {},
                headerVariableValues: {},
                buttonVariables: {},
                previewTextBody: "",
                previewTextHeader: "",
            });
            return;
        }

        // Propriedades em minúsculo conforme retorno da API
        const bodyVariables = this.extractVariables(value.bodytext);
        const headerVariables = this.extractVariables(value.headertext);
        const buttonVariables = this.extractButtonVariables(value.templatemessagebuttons);

        const bodyVariableValues = bodyVariables.reduce((acc, v) => ({ ...acc, [v]: "" }), {});
        const headerVariableValues = headerVariables.reduce((acc, v) => ({ ...acc, [v]: "" }), {});

        this.setState({
            selectedTemplate: value,
            bodyVariableValues,
            headerVariableValues,
            buttonVariables,
            previewTextBody: value.bodytext || "",
            previewTextHeader: value.headertext || "",
        });
    };

    extractVariables = (text) => {
        if (!text) return [];
        const regex = /\{\{(\d+)\}\}/g;
        const variables = new Set();
        let match;
        while ((match = regex.exec(text))) {
            variables.add(match[1]);
        }
        return Array.from(variables);
    };

    extractButtonVariables = (buttons) => {
        if (!buttons || buttons.length === 0) return {};
        const buttonVars = {};
        buttons.forEach((button) => {
            const vars = this.extractVariables(button.url);
            if (vars.length > 0) {
                buttonVars[button.id] = vars.reduce((acc, v) => ({ ...acc, [v]: "" }), {});
            }
        });
        return buttonVars;
    };

    handleVariableChange = (type, variable, value) => {
        this.setState(
            (prevState) => ({
                [`${type}VariableValues`]: {
                    ...prevState[`${type}VariableValues`],
                    [variable]: value,
                },
            }),
            () => {
                if (type === "body") this.updatePreviewBody();
                else if (type === "header") this.updatePreviewHeader();
            }
        );
    };

    handleButtonVariableChange = (buttonId, variable, value) => {
        this.setState((prevState) => ({
            buttonVariables: {
                ...prevState.buttonVariables,
                [buttonId]: {
                    ...prevState.buttonVariables[buttonId],
                    [variable]: value,
                },
            },
        }));
    };

    updatePreviewBody = () => {
        const { selectedTemplate, bodyVariableValues } = this.state;
        if (!selectedTemplate?.bodytext) return;

        let updatedText = selectedTemplate.bodytext;
        Object.entries(bodyVariableValues).forEach(([key, value]) => {
            if (value) {
                updatedText = updatedText.replace(`{{${key}}}`, value);
            }
        });
        this.setState({ previewTextBody: updatedText });
    };

    updatePreviewHeader = () => {
        const { selectedTemplate, headerVariableValues } = this.state;
        if (!selectedTemplate?.headertext) return;

        let updatedText = selectedTemplate.headertext;
        Object.entries(headerVariableValues).forEach(([key, value]) => {
            if (value) {
                updatedText = updatedText.replace(`{{${key}}}`, value);
            }
        });
        this.setState({ previewTextHeader: updatedText });
    };

    hasEmptyValues = (obj) => {
        return Object.values(obj).some(value => {
            if (typeof value === "object" && value !== null) {
                return this.hasEmptyValues(value);
            }
            return !(typeof value === "string" && value.trim() !== "");
        });
    };

    validateForm = () => {
        const { 
            selectedRegistrationNumber, 
            selectedMessageProvider, 
            selectedTemplate,
            bodyVariableValues,
            headerVariableValues,
            buttonVariables 
        } = this.state;
        const { onError } = this.props;

        const { intl } = this.props;

        if (!selectedRegistrationNumber?.id) {
            onError && onError(intl.formatMessage({ id: "error.select.registered.number" }));
            return false;
        }

        if (!selectedMessageProvider?.id) {
            onError && onError(intl.formatMessage({ id: "error.select.message.provider" }));
            return false;
        }

        if (!selectedTemplate?.id) {
            onError && onError(intl.formatMessage({ id: "error.select.message.template" }));
            return false;
        }

        const allVariables = { ...bodyVariableValues, ...buttonVariables };
        if (this.hasEmptyValues(allVariables) || this.hasEmptyValues(headerVariableValues)) {
            onError && onError(intl.formatMessage({ id: "error.fill.all.variables" }));
            return false;
        }

        return true;
    };

    handleSubmit = async () => {
        if (!this.validateForm()) return;

        const { onSubmit, phoneNumber, userId } = this.props;
        const {
            selectedRegistrationNumber,
            selectedMessageProvider,
            selectedTemplate,
            bodyVariableValues,
            headerVariableValues,
            buttonVariables,
        } = this.state;

        this.setState({ loadingSubmit: true });

        // Dados enviados em minúsculo para a API
        const data = {
            registrationnumberrequestid: selectedRegistrationNumber.id,
            messageproviderid: selectedMessageProvider.id,
            templatemessageid: selectedTemplate.id,
            phone: phoneNumber,
            bodyvariablevalues: bodyVariableValues,
            headervariablevalues: headerVariableValues,
            buttonvariablevalue: buttonVariables,
            recipientuserid: userId || null,
        };

        try {
            await onSubmit(data);
        } finally {
            this.setState({ loadingSubmit: false });
        }
    };

    handleClose = () => {
        this.setState({
            selectedRegistrationNumber: null,
            selectedMessageProvider: null,
            selectedTemplate: null,
            registrationNumberList: [],
            messageProviderList: [],
            templateList: [],
            bodyVariableValues: {},
            headerVariableValues: {},
            buttonVariables: {},
            previewTextBody: "",
            previewTextHeader: "",
            canSubmit: false,
        });
        this.props.onClose();
    };

    renderTemplateFields = () => {
        const { classes, intl } = this.props;
        const { 
            selectedTemplate, 
            headerVariableValues, 
            bodyVariableValues, 
            buttonVariables,
            previewTextHeader,
            previewTextBody 
        } = this.state;

        if (!selectedTemplate) return null;
        PapiroConsole.log("selectedTemplate aqui ")
        PapiroConsole.log(selectedTemplate)

        return (
            <>
                {/* Header */}
                {selectedTemplate.headertext && (
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>{intl.formatMessage({ id: "header.section" })}</h4>
                        <p className={classes.sectionText}>{previewTextHeader}</p>
                        {this.extractVariables(selectedTemplate.headertext).map((variable) => (
                            <TextField
                                required
                                key={`header-${variable}`}
                                label={`Valor para {{${variable}}}`}
                                value={headerVariableValues[variable] || ""}
                                onChange={(e) => this.handleVariableChange("header", variable, e.target.value)}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                size="small"
                            />
                        ))}
                    </Grid>
                )}

                {/* Body */}
                {selectedTemplate.bodytext && (
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>{intl.formatMessage({ id: "message.body.section" })}</h4>
                        <p className={classes.sectionText}>{previewTextBody}</p>
                        {this.extractVariables(selectedTemplate.bodytext).map((variable) => (
                            <TextField
                                required
                                key={`body-${variable}`}
                                label={`Valor para {{${variable}}}`}
                                value={bodyVariableValues[variable] || ""}
                                onChange={(e) => this.handleVariableChange("body", variable, e.target.value)}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                size="small"
                            />
                        ))}
                    </Grid>
                )}

                {/* Buttons */}
                {selectedTemplate.templatemessagebuttons?.length > 0 && (
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>{intl.formatMessage({ id: "buttons.section" })}</h4>
                        {selectedTemplate.templatemessagebuttons.map((button) => {
                            const buttonVars = this.extractVariables(button.url);
                        
                            
                            return (
                                <div key={button.id} style={{ marginBottom: "12px" }}>
                                    <p className={classes.sectionText}>
                                        {button.type === 2 ? button.label : button.url}
                                    </p>
                                    {buttonVars.map((variable) => (
                                        <TextField
                                            required
                                            key={`button-${button.id}-${variable}`}
                                            label={`Valor para {{${variable}}}`}
                                            value={buttonVariables[button.id]?.[variable] || ""}
                                            onChange={(e) => this.handleButtonVariableChange(button.id, variable, e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            size="small"
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </Grid>
                )}
            </>
        );
    };

    renderActiveChats = () => {
        const { classes, intl, onOpenChat, initializationData } = this.props;
        const activeChats = initializationData?.activechatlist || [];

        if (activeChats.length === 0) return null;

        return (
            <div className={classes.activeChatSection}>
                <h4 className={classes.activeChatTitle}>
                    {intl.formatMessage({ id: "active.chats.section.title" })}
                </h4>
                {activeChats.map((chat) => (
                    <div
                        key={chat.chatid}
                        className={classes.activeChatCard}
                        onClick={() => onOpenChat && onOpenChat(chat.chatid)}
                    >
                        <span className={classes.activeChatPhone}>
                            {chat.phonenumber}
                        </span>
                        <span className={classes.activeChatLabel}>
                            {intl.formatMessage({ id: "active.chat.click.to.open" })}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const { open, classes, intl } = this.props;
        const {
            registrationNumberList,
            messageProviderList,
            templateList,
            selectedRegistrationNumber,
            selectedMessageProvider,
            selectedTemplate,
            loadingTemplates,
            loadingSubmit,
            canSubmit,
        } = this.state;

        const isSubmitDisabled = !canSubmit || !selectedTemplate || loadingSubmit;

        return (
            <Dialog open={open} onClose={this.handleClose} fullWidth maxWidth="sm">
                <DialogTitle className={classes.dialogTitle} sx={{ position: "relative" }}>
                    {intl.formatMessage({ id: "start.whatsapp.conversation" })}
                    <IconButton 
                        sx={{ position: "absolute", right: 8, top: 8 }} 
                        onClick={this.handleClose}
                    >
                        <CloseIcon sx={{ color: 'white' }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={2} className={classes.gridContainer}>
                        {/* Número Registrado */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={registrationNumberList}
                                getOptionLabel={(option) => option.phonenumber || ""}
                                onChange={this.handleRegistrationNumberChange}
                                value={selectedRegistrationNumber}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label={intl.formatMessage({ id: "registered.number.label" })}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Provedor de Mensagem */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={messageProviderList}
                                getOptionLabel={(option) => option.name || ""}
                                onChange={this.handleMessageProviderChange}
                                value={selectedMessageProvider}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label={intl.formatMessage({ id: "message.provider.label" })}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Modelos de Mensagem */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={templateList}
                                getOptionLabel={(option) => option.name || option.label || ""}
                                onChange={this.handleTemplateChange}
                                value={selectedTemplate}
                                loading={loadingTemplates}
                                disabled={!selectedRegistrationNumber || loadingTemplates}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label={intl.formatMessage({ id: "message.template.label" })}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingTemplates && <CircularProgress size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        helperText={!selectedRegistrationNumber ? intl.formatMessage({ id: "select.registered.number.first" }) : ""}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Campos de variáveis do template */}
                        {this.renderTemplateFields()}
                    </Grid>
                    {this.renderActiveChats()}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={this.handleClose}
                        variant="outlined"
                        color="primary"
                    >
                        {intl.formatMessage({ id: "close" })}
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitDisabled}
                    >
                        {loadingSubmit ? <CircularProgress size={24} /> : intl.formatMessage({ id: "start.conversation" })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

StartConversationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onOpenChat: PropTypes.func,
    initializationData: PropTypes.shape({
        registrationnumberrequestlist: PropTypes.array,
        messageproviderlist: PropTypes.array,
        activechatlist: PropTypes.array,
    }),
    phoneNumber: PropTypes.string.isRequired,
    userId: PropTypes.number,
    validatePhoneApi: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    intl: PropTypes.object,
};

export default withStyles(styles)(StartConversationModal);